import React from "react";
import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Sparkles, Globe, ScanLine, Wand2, ClipboardList, Languages, Mic,
  KanbanSquare, Brain, IdCard, Briefcase, Check, TrendingUp, Users, Building2, Search,
} from "lucide-react";
import { Reveal, SectionLabel, CountUp, MatchRing, GradientText } from "@/components/ui-bits";
import JobCard from "@/components/JobCard";
import PricingCards from "@/components/PricingCards";
import AboutSection from "@/components/AboutSection";
import { jobs, atsSeed, jdSeed, autofillFields, cvLanguages, comparison } from "@/data/mockData";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";

export default function Landing() {
  const { user, ready } = useAuth() || {};

  if (ready && user) {
    return <Navigate to={user.role === "recruiter" ? "/recruiter" : "/app"} replace />;
  }

  return (
    <div className="relative">
      <Hero />
      <CustomizationSection />
      <LanguageSection />
      <JobsSection />
      <ATSSection />
      <InterviewSection />
      <AutofillSection />
      <EcosystemSection />
      <ComparisonTeaser />
      <AboutSection />
      <PricingSection />
      <FinalCTA />
    </div>
  );
}

/* ---------------------------------------------------------------- HERO */
function Hero() {
  const { t } = useLanguage();

  const heroChips = [
    t("hero_customised_cv", "Customised CV"),
    t("hero_cv_4_languages", "CV in 4 Languages"),
    t("hero_ats_check", "ATS Check"),
    t("hero_interview_practice", "Interview Practice"),
    t("hero_job_search", "Job Search"),
  ];

  return (
    <section className="relative overflow-hidden pt-16 pb-24 sm:pt-24">
      <div className="cv-container grid items-center gap-12 lg:grid-cols-2">
        <div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card/70 px-4 py-1.5 text-xs font-semibold backdrop-blur">
              <Sparkles className="h-3.5 w-3.5 text-violet" /> {t("hero_badge", "Career, Evolved · From CV to interview")}
            </span>
            <h1 className="mt-6 font-heading text-5xl font-black leading-[0.95] tracking-tighter sm:text-6xl lg:text-[4.5rem]">
              {t("hero_title_1", "Let your CV speak for you.")}
              <br />
              <GradientText>{t("hero_title_2", "CVOLVE helps you make it heard.")}</GradientText>
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground">
              {t("hero_sub", "Create a CV built specifically for the job you want. CVOLVE matches your experience with the Job Description, optimises your ATS score, prepares you with personalised interview questions and live interview practice, and helps you discover and apply for the right job opportunities, all in one platform.")}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/signup" className="cv-btn-primary text-base" data-testid="hero-start-free">
                {t("nav_start_free", "Start Free")} <ArrowRight className="h-4 w-4" />
              </Link>
              <a href="#jobs" className="cv-btn-ghost border border-border text-base" data-testid="hero-explore-jobs">
                {t("hero_explore_jobs", "Explore Jobs")}
              </a>
            </div>
            <div className="mt-8 flex flex-wrap gap-2">
              {heroChips.map((c) => (
                <span key={c} className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1.5 text-xs font-semibold text-muted-foreground">
                  <Check className="h-3 w-3 text-lime" /> {c}
                </span>
              ))}
            </div>
          </motion.div>
        </div>

        <HeroComposition />
      </div>
    </section>
  );
}

function HeroComposition() {
  return (
    <div className="relative h-[520px]">
      {/* Passport card */}
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
        className="absolute left-0 top-4 w-72 rounded-3xl border border-border bg-card p-5 shadow-float animate-float">
        <div className="flex items-center gap-3">
          <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=entropy&cs=srgb&fm=jpg&w=200&q=80" alt="Alex" className="h-12 w-12 rounded-full object-cover" />
          <div>
            <p className="font-heading font-bold leading-tight">Alex Morgan</p>
            <p className="text-xs text-muted-foreground">Product Analyst → PM</p>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between rounded-2xl bg-gradient-to-br from-electric to-violet p-4 text-white">
          <div>
            <p className="text-xs opacity-80">Profile strength</p>
            <p className="font-heading text-2xl font-black"><CountUp to={86} suffix="%" /></p>
          </div>
          <IdCard className="h-8 w-8 opacity-80" />
        </div>
      </motion.div>

      {/* Match card */}
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
        className="absolute right-0 top-0 w-60 rounded-3xl border border-border bg-card p-5 shadow-float animate-float-slow">
        <p className="label-caps">Top match</p>
        <p className="mt-1 font-heading text-lg font-bold">Product Manager</p>
        <p className="text-sm text-muted-foreground">Nimbus Labs · London</p>
        <div className="mt-3 flex items-center gap-3">
          <MatchRing value={94} size={52} stroke={6} color="hsl(var(--chart-4))" />
          <span className="text-sm font-semibold text-lime">94% match</span>
        </div>
      </motion.div>

      {/* ATS card */}
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
        className="absolute bottom-16 right-6 w-56 rounded-3xl border border-border bg-card p-5 shadow-float">
        <p className="label-caps">ATS score</p>
        <p className="font-heading text-4xl font-black text-electric"><CountUp to={87} /></p>
        <div className="mt-3 space-y-1.5">
          {[["Keywords", 78], ["Formatting", 94], ["Alignment", 72]].map(([l, v]) => (
            <div key={l}>
              <div className="flex justify-between text-[11px] text-muted-foreground"><span>{l}</span><span>{v}%</span></div>
              <div className="h-1.5 rounded-full bg-secondary">
                <motion.div className="h-full rounded-full bg-aqua" initial={{ width: 0 }} animate={{ width: `${v}%` }} transition={{ delay: 0.8, duration: 1 }} />
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Interview chip */}
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.65 }}
        className="absolute bottom-0 left-6 flex items-center gap-3 rounded-2xl border border-border bg-card p-4 shadow-float">
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-violet/15"><Mic className="h-5 w-5 text-violet" /></span>
        <div>
          <p className="text-sm font-bold">Interview Copilot</p>
          <p className="text-xs text-muted-foreground">STAR feedback ready</p>
        </div>
      </motion.div>
    </div>
  );
}

/* --------------------------------------------------- SECTION WRAPPER */
function Section({ id, children, className = "" }) {
  return <section id={id} className={`cv-container py-20 sm:py-28 ${className}`}>{children}</section>;
}
function Heading({ label, color, title, sub }) {
  return (
    <Reveal className="mb-14 max-w-3xl">
      {label && <div className="mb-4"><SectionLabel color={color}>{label}</SectionLabel></div>}
      <h2 className="font-heading text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl">{title}</h2>
      {sub && <p className="mt-4 text-lg text-muted-foreground">{sub}</p>}
    </Reveal>
  );
}

/* -------------------------------------------- 3. JOBS */
function JobsSection() {
  const [liveJobs, setLiveJobs] = React.useState([]);

  React.useEffect(() => {
    let active = true;
    const fetchLandingJobs = async () => {
      try {
        const { data } = await import("@/lib/api").then((m) => m.api.get("/jobs", { params: { country: "all" } }));
        if (active && data?.jobs && data.jobs.length > 0) {
          setLiveJobs(data.jobs.slice(0, 3));
        }
      } catch (_) {}
    };
    fetchLandingJobs();
    return () => { active = false; };
  }, []);

  return (
    <Section id="jobs">
      <Heading label="Global Job Aggregator" color="text-aqua"
        title="Search Jobs on CVOLVE. Discover Opportunities Worldwide. Get Matched."
        sub="CVOLVE brings job opportunities from across the web into one place, so you can search for the roles you want, discover opportunities worldwide, and get matched with jobs aligned to your skills, experience and career goals. Onsite, remote or hybrid, your next opportunity starts here." />
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {(liveJobs.length > 0 ? liveJobs : jobs.slice(0, 3)).map((j) => <JobCard key={j.id} job={j} />)}
      </div>
      <Reveal delay={0.1}>
        <div className="mt-8"><Link to="/signup" className="cv-btn-primary">See all global matches <ArrowRight className="h-4 w-4" /></Link></div>
      </Reveal>
    </Section>
  );
}

/* -------------------------------------------- 4. ATS */
function ATSSection() {
  const bars = [
    ["Keywords", atsSeed.keywords_score], ["Skills", atsSeed.skills_score], ["Experience", atsSeed.experience_score],
    ["Formatting", atsSeed.formatting_score], ["JD alignment", atsSeed.jd_alignment_score],
  ];
  return (
    <Section id="ats">
      <Heading label="ATS Intelligence" color="text-coral" title="CVOLVE Checks Your ATS Score and Gives You Actionable Insights."
        sub="Don’t just check your ATS score. CVOLVE shows you what’s working, what’s missing and where your CV can improve, from keywords and skills to experience, formatting and job description alignment." />
      <div className="grid gap-8 lg:grid-cols-2">
        <Reveal>
          <div className="card-surface flex flex-col items-center p-10 text-center">
            <MatchRing value={atsSeed.score} size={180} stroke={14} color="hsl(var(--chart-1))" label={<CountUp to={atsSeed.score} />} />
            <p className="mt-4 font-heading text-xl font-bold">Overall ATS Score</p>
            <p className="mt-2 max-w-sm text-sm text-muted-foreground">{atsSeed.summary}</p>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="space-y-4">
            {bars.map(([l, v]) => (
              <div key={l} className="card-surface p-4">
                <div className="flex justify-between text-sm font-semibold"><span>{l}</span><span className="text-electric"><CountUp to={v} suffix="%" /></span></div>
                <div className="mt-2 h-2.5 rounded-full bg-secondary">
                  <motion.div className="h-full rounded-full bg-gradient-to-r from-electric to-aqua" initial={{ width: 0 }} whileInView={{ width: `${v}%` }} viewport={{ once: true }} transition={{ duration: 1.1 }} />
                </div>
              </div>
            ))}
            <div className="card-surface p-4">
              <p className="label-caps mb-2 text-coral">Missing requirements</p>
              <div className="flex flex-wrap gap-2">
                {atsSeed.missing_keywords.map((k) => (
                  <span key={k} className="rounded-full bg-coral/10 px-3 py-1 text-xs font-semibold text-coral">{k}</span>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </Section>
  );
}

/* -------------------------------------------- 5. CUSTOMIZATION */
function CustomizationSection() {
  return (
    <Section id="customize">
      <Heading label="Intelligent CV Customization" color="text-electric"
        title="Your Experience Is Unique. Your CV Should Be Too."
        sub="CVOLVE reads the job description, finds real gaps, asks you targeted questions — then rewrites your CV. Never fabricated." />
      <div className="grid gap-6 lg:grid-cols-3">
        <Reveal>
          <div className="card-surface h-full p-5">
            <p className="label-caps mb-3">Job Description</p>
            <p className="text-sm leading-relaxed text-muted-foreground">{jdSeed.jobDescription}</p>
            <div className="mt-4 space-y-2">
              {jdSeed.mismatches.slice(0, 2).map((m, i) => (
                <p key={i} className="rounded-lg bg-coral/10 px-3 py-2 text-xs font-medium text-coral">⚠ {m}</p>
              ))}
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="card-surface h-full p-5">
            <p className="label-caps mb-3 text-violet">Targeted question</p>
            <div className="rounded-2xl bg-violet/5 p-4">
              <p className="font-heading font-bold">"{jdSeed.questions[0]}"</p>
            </div>
            <div className="mt-4 rounded-2xl border border-border p-4">
              <p className="label-caps mb-1">Your answer</p>
              <p className="text-sm text-muted-foreground">Led a 4-team enterprise rollout for our top 5 accounts, aligning stakeholders across sales, CS and eng.</p>
            </div>
            <p className="mt-3 flex items-center gap-2 text-xs font-semibold text-lime"><Check className="h-4 w-4" /> Answer captured</p>
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="rounded-3xl border border-electric/30 bg-card p-5 shadow-glow">
            <p className="label-caps mb-3 text-electric">Customized CV</p>
            <div className="space-y-2 text-sm">
              <p className="line-through text-muted-foreground">Analysed checkout funnel data.</p>
              <p className="rounded-lg bg-electric/10 px-3 py-2 font-medium">Owned enterprise stakeholder alignment across 4 teams, driving a 14% conversion lift for top-5 accounts.</p>
            </div>
            <div className="mt-4 flex items-center justify-between rounded-xl bg-secondary p-3">
              <span className="text-xs font-semibold text-muted-foreground">JD alignment</span>
              <span className="font-heading font-bold text-lime">74% → 93%</span>
            </div>
          </div>
        </Reveal>
      </div>
    </Section>
  );
}

/* -------------------------------------------- 6. AUTOFILL */
function AutofillSection() {
  return (
    <Section id="autofill">
      <Heading label="Application Autofill" color="text-aqua" title="Stop typing your career twice."
        sub="CVOLVE completes long application forms from your Career Profile in seconds." />
      <div className="grid gap-8 lg:grid-cols-2 lg:items-center">
        <Reveal>
          <div className="card-surface p-6">
            <div className="grid grid-cols-2 gap-3">
              {autofillFields.map((f, i) => (
                <motion.div key={f.label} initial={{ opacity: 0.3 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                  className="rounded-xl border border-border bg-background p-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{f.label}</p>
                  <p className="truncate text-sm font-semibold">{f.value}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-lime/15 px-4 py-2 text-sm font-bold text-lime">
              <Check className="h-4 w-4" /> <CountUp to={27} /> fields completed automatically
            </div>
            <h3 className="mt-6 font-heading text-2xl font-extrabold">One profile. Every application.</h3>
            <p className="mt-3 text-muted-foreground">Name, contact, experience, education, skills, LinkedIn, portfolio, work authorization — filled instantly, everywhere you apply.</p>
            <Link to="/signup" className="cv-btn-primary mt-6">Try autofill <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </Reveal>
      </div>
    </Section>
  );
}

/* -------------------------------------------- 7. LANGUAGES */
function LanguageSection() {
  return (
    <Section id="languages">
      <Heading label="4-Language CV" color="text-violet" title="One Profile. Four Languages. Global Careers."
        sub="Generate a polished, native-quality CV in English, Spanish, German and French from the same Career Profile." />
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {cvLanguages.map((c, i) => (
          <Reveal key={c.code} delay={i * 0.08}>
            <div className="card-surface overflow-hidden">
              <div className="flex items-center justify-between bg-gradient-to-r from-electric/10 to-violet/10 px-4 py-3">
                <span className="text-2xl">{c.flag}</span>
                <span className="label-caps">{c.name}</span>
              </div>
              <div className="p-4">
                <p className="font-heading text-lg font-bold">{c.title}</p>
                <p className="mt-2 text-sm text-muted-foreground">{c.summary}</p>
                <div className="mt-4 space-y-2">
                  {[90, 70, 80].map((w, k) => <div key={k} className="h-2 rounded-full bg-secondary" style={{ width: `${w}%` }} />)}
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}

/* -------------------------------------------- 8. INTERVIEW */
function InterviewSection() {
  const star = ["Situation", "Task", "Action", "Result"];
  return (
    <Section id="interview">
      <Heading label="Interview" color="text-pink" title="Know What They’ll Ask. Practice How You’ll Answer."
        sub="CVOLVE prepares you for the questions you’re likely to face, helps you structure stronger STAR answers, and lets you practise in a realistic face-to-face interview. Get feedback, refine your answers and be ready when it matters." />
      <div className="grid gap-6 lg:grid-cols-3">
        <Reveal className="lg:col-span-2">
          <div className="card-surface aspect-video overflow-hidden p-0">
            <div className="relative flex h-full items-center justify-center bg-gradient-to-br from-navy to-[#131c3a]">
              <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=entropy&cs=srgb&fm=jpg&w=200&q=80" alt="candidate" className="h-24 w-24 rounded-full object-cover ring-4 ring-white/20" />
              <span className="absolute bottom-4 left-4 rounded-full bg-black/40 px-3 py-1 text-xs font-semibold text-white backdrop-blur">● Practice room · live transcription</span>
              <span className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white"><Mic className="h-5 w-5" /></span>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="card-surface h-full p-5">
            <p className="label-caps mb-3">STAR structure</p>
            <div className="space-y-3">
              {star.map((s, i) => (
                <div key={s} className="flex items-center gap-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-pink/15 font-heading font-bold text-pink">{s[0]}</span>
                  <span className="text-sm font-semibold">{s}</span>
                  <motion.div className="ml-auto h-2 rounded-full bg-lime" initial={{ width: 0 }} whileInView={{ width: 48 }} viewport={{ once: true }} transition={{ delay: i * 0.2 }} />
                </div>
              ))}
            </div>
            <p className="mt-5 rounded-xl bg-secondary p-3 text-xs text-muted-foreground">"Quantify your result — add the 14% conversion metric to land the impact."</p>
          </div>
        </Reveal>
      </div>
    </Section>
  );
}

/* -------------------------------------------- 10. ECOSYSTEM */
function EcosystemSection() {
  return (
    <Section id="how">
      <Heading label="One Ecosystem" color="text-electric" title="Talent and opportunity. One ecosystem."
        sub="CVOLVE connects job seekers and recruiters through the same intelligent platform." />
      <div className="grid gap-6 lg:grid-cols-2">
        <Reveal>
          <div className="rounded-3xl border border-border bg-navy p-8 text-cream">
            <div className="flex items-center gap-2"><Users className="h-5 w-5 text-aqua" /><p className="label-caps text-aqua">Job Seeker</p></div>
            <h3 className="mt-3 font-heading text-2xl font-extrabold">Move your career forward</h3>
            <ul className="mt-5 space-y-3 text-sm">
              {["Career Profile", "Find Jobs", "AI Applications", "Interview practice"].map((x) => (
                <li key={x} className="flex items-center gap-2"><Check className="h-4 w-4 text-lime" /> {x}</li>
              ))}
            </ul>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="rounded-3xl border border-border bg-cream p-8 text-navy dark:bg-card dark:text-foreground">
            <div className="flex items-center gap-2"><Building2 className="h-5 w-5 text-violet" /><p className="label-caps text-violet">Recruiter</p></div>
            <h3 className="mt-3 font-heading text-2xl font-extrabold">Find talent that fits</h3>
            <ul className="mt-5 space-y-3 text-sm">
              {["Post Jobs", "Candidate Search", "AI Matching", "Pipeline & Interview"].map((x) => (
                <li key={x} className="flex items-center gap-2"><Check className="h-4 w-4 text-electric" /> {x}</li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>
    </Section>
  );
}

/* -------------------------------------------- 11. COMPARISON TEASER */
function ComparisonTeaser() {
  const rows = comparison.rows.slice(0, 6);
  return (
    <Section id="compare-teaser">
      <Heading label="Comparison" color="text-violet" title="Why use five platforms when your career can have one home?"
        sub="CVOLVE replaces the job board, the network, the resume tool, the ATS checker and the interview app." />
      <Reveal>
        <div className="overflow-x-auto rounded-3xl border border-border">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="border-b border-border bg-secondary/50">
                <th className="p-4 text-left font-heading">Capability</th>
                {comparison.categories.map((c) => (
                  <th key={c} className={`p-4 text-center font-heading ${c === "CVOLVE" ? "text-electric" : "text-muted-foreground"}`}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={r} className="border-b border-border last:border-0">
                  <td className="p-4 font-medium">{r}</td>
                  {comparison.categories.map((c) => {
                    const v = comparison.matrix[c][ri];
                    return (
                      <td key={c} className={`p-4 text-center ${c === "CVOLVE" ? "bg-electric/5" : ""}`}>
                        {v === 2 ? <Check className="mx-auto h-[18px] w-[18px] text-[#15803d] dark:text-lime" strokeWidth={3} /> : v === 1 ? <span className="text-xs font-medium text-muted-foreground">partial</span> : <span className="text-muted-foreground">—</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-6 flex items-center gap-4">
          <Link to="/compare" className="cv-btn-primary">See full comparison <ArrowRight className="h-4 w-4" /></Link>
          <span className="text-xs text-muted-foreground">Feature availability varies.</span>
        </div>
      </Reveal>
    </Section>
  );
}

/* -------------------------------------------- 12. PRICING */
function PricingSection() {
  return (
    <Section id="pricing">
      <Heading
        label="Pricing"
        color="text-electric"
        title="Plans that evolve with you."
        sub="Start free. Upgrade when your career demands more firepower."
      />
      <PricingCards />
    </Section>
  );
}

/* -------------------------------------------- FINAL CTA */
function FinalCTA() {
  return (
    <section className="cv-container py-24">
      <Reveal>
        <div className="relative overflow-hidden rounded-[2rem] bg-navy px-8 py-20 text-center text-cream">
          <div className="absolute -top-24 left-1/4 h-72 w-72 rounded-full bg-electric/30 blur-3xl" />
          <div className="absolute -bottom-24 right-1/4 h-72 w-72 rounded-full bg-violet/30 blur-3xl" />
          <div className="relative">
            <h2 className="mx-auto max-w-3xl font-heading text-3xl font-black leading-tight tracking-tight sm:text-5xl">
              Your career shouldn't live across six different platforms.
            </h2>
            <p className="mx-auto mt-6 flex flex-wrap justify-center gap-x-4 gap-y-1 font-heading text-lg font-bold text-aqua">
              <span>Search.</span><span>Evolve.</span><span>Apply.</span><span>Prepare.</span><span>Move forward.</span>
            </p>
            <Link to="/signup" className="mt-10 inline-flex items-center gap-2 rounded-full bg-cream px-8 py-4 text-base font-bold text-navy transition-transform hover:scale-105" data-testid="final-start-free">
              Start Free <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
